//Types
import type { Device } from 'src/types/Device';
import type {
	WebHookConditionOption,
	WebHookEventOption,
	WebHooksParams,
} from 'src/types/requests/settings/webhooks';

type CreateWebhookWizardStepIndexes = {
	component: number;
	name: number;
	time: number;
	condition: number;
	actions: number;
};

type CreateWebhookConditionResult = {
	isValid: boolean;
	condition: string;
};

export const usesSupportedWebhookAttribute = (condition: string, tokens: string[] = []): boolean =>
	tokens.some((token) => condition.includes(token));

export const getCreateWebhookWizardStepIndexes = (
	chooseComponent: boolean,
	hideTimePicker: boolean,
): CreateWebhookWizardStepIndexes => {
	if (chooseComponent) {
		if (hideTimePicker) {
			return {
				component: 0,
				name: 1,
				time: -1,
				condition: 2,
				actions: 3,
			};
		}
		return {
			component: 0,
			name: 1,
			time: 2,
			condition: 3,
			actions: 4,
		};
	}
	if (hideTimePicker) {
		return {
			component: -1,
			name: 0,
			time: -1,
			condition: 1,
			actions: 2,
		};
	}
	return {
		component: -1,
		name: 0,
		time: 1,
		condition: 2,
		actions: 3,
	};
};

export const getCreateWebhookDisabledConditions = (
	wizardItemsLength: number,
	stepIndexes: CreateWebhookWizardStepIndexes,
	isNameEmpty: boolean,
	hasValidConditions: boolean,
	hasValidUrls: boolean,
): boolean[] => {
	const conditionDisabled = !hasValidConditions || isNameEmpty;
	const urlsDisabled = !hasValidUrls;
	const conditions = Array(wizardItemsLength).fill(false);

	conditions[stepIndexes.name] = isNameEmpty;
	if (stepIndexes.time >= 0) {
		conditions[stepIndexes.time] = isNameEmpty;
	}
	conditions[stepIndexes.condition] = conditionDisabled;
	conditions[stepIndexes.actions] = urlsDisabled;
	return conditions;
};

export const getProEmWebhookCid = (
	event: string,
	hasPeripherals: boolean,
	deviceChannel: number | undefined,
): number => {
	if (hasPeripherals) {
		return 100;
	}
	if (event.startsWith('switch.')) {
		return 0;
	}
	return deviceChannel ?? 0;
};

export const buildCreateWebhookData = (
	actionProps: WebHooksParams,
	eventProps: WebHookEventOption,
	device: Device,
	hideTimePicker: boolean,
	hasPeripherals: boolean,
): WebHooksParams => {
	const data: WebHooksParams = {
		cid: actionProps.cid,
		id: actionProps.id,
		enable: actionProps.enable !== false,
		name: actionProps.name,
		event: eventProps.event,
		condition: '',
		urls: actionProps.urls,
	};

	if (/^(SPEM-002CEBEU50)$/.test(device.type)) {
		data.cid = getProEmWebhookCid(data.event, hasPeripherals, device.channel);
	}
	if (!hideTimePicker) {
		data.active_between = actionProps.set_active_time ? actionProps.active_between : null;
	}
	if (actionProps.repeat_period != null) {
		data.repeat_period = actionProps.repeat_period;
	}
	return data;
};

export const getCreateWebhookConditionExpression = (conditionOption: WebHookConditionOption): string => {
	if (conditionOption.isRawExpression) {
		return String(conditionOption.conditionValue || '').trim();
	}
	const selectedCondition = conditionOption.conditions?.[conditionOption.conditionsIndex];
	let event = conditionOption.conditionEvent;
	if (selectedCondition?.conditionEvent != null) {
		event = selectedCondition.conditionEvent;
	}
	return `${event} ${selectedCondition.value} ${conditionOption.conditionValue}`.trim();
};

export const buildCreateWebhookCondition = (
	eventProps: WebHookEventOption,
): CreateWebhookConditionResult => {
	if (!eventProps?.conditions) {
		return { isValid: true, condition: '' };
	}

	const conditions: string[] = [];
	for (const conditionOption of eventProps.conditions) {
		if (conditionOption.conditionsIndex === -1) {
			continue;
		}
		if (String(conditionOption.conditionValue || '').trim() === '') {
			return { isValid: false, condition: '' };
		}
		conditions.push(getCreateWebhookConditionExpression(conditionOption));
	}

	return { isValid: true, condition: conditions.join(' && ') };
};

export const hasValidCreateWebhookConditions = (eventProps: WebHookEventOption): boolean => {
	const conditionResult = buildCreateWebhookCondition(eventProps);
	if (!conditionResult.isValid) return false;
	if (!eventProps?.event?.startsWith('lnm.')) return true;
	return eventProps.conditions?.some((condition) => condition.conditionsIndex >= 0) ?? false;
};

export const hasValidCreateWebhookUrls = (
	urls: string[],
	isValidUrl: (url: string, allowLocalhost?: boolean) => boolean,
): boolean => {
	return urls.every((url) => isValidUrl(url, true));
};
